import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Syringe, Calendar, CheckCircle, AlertCircle, Clock, Plus, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { useViewOnly } from '../contexts/ViewOnlyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Vaccinations = ({ baby }) => {
  const navigate = useNavigate();
  // Always read-only for baby/parent users - only doctors can edit
  const viewOnlyMode = true;
  const [vaccinations, setVaccinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVaccination, setEditingVaccination] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [showSchedule, setShowSchedule] = useState(true);
  const [addFormData, setAddFormData] = useState({
    vaccine_name: '',
    trade_name: '',
    given_date: ''
  });
  const [formData, setFormData] = useState({
    given_date: '',
    batch_number: '',
    expiry_date: '',
    trade_name: ''
  });

  // Handle date input with auto-advance
  const handleDateInput = (field) => (e) => {
    let value = e.target.value;
    
    // Allow backspace/delete to work naturally
    if (e.nativeEvent.inputType === 'deleteContentBackward' || e.nativeEvent.inputType === 'deleteContentForward') {
      setFormData(prev => ({ ...prev, [field]: value }));
      return;
    }
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle date input for add form
  const handleAddDateInput = (field) => (e) => {
    let value = e.target.value;
    
    // Allow backspace/delete to work naturally
    if (e.nativeEvent.inputType === 'deleteContentBackward' || e.nativeEvent.inputType === 'deleteContentForward') {
      setAddFormData(prev => ({ ...prev, [field]: value }));
      return;
    }
    
    value = value.replace(/[^\d/]/g, '');
    
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setAddFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle backspace for natural deletion
  const handleDateKeyDown = (field) => (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setFormData(prev => ({ ...prev, [field]: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  useEffect(() => {
    fetchVaccinations();
  }, [baby]);

  const fetchVaccinations = async () => {
    try {
      const response = await axios.get(`${API}/vaccinations/${baby.id}`);
      const sortedVaccinations = response.data.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      setVaccinations(sortedVaccinations);
    } catch (error) {
      console.error('Error fetching vaccinations:', error);
      toast.error('Failed to load vaccination records');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSchedule = async () => {
    if (!window.confirm('This will add any missing vaccinations to the schedule. Existing vaccinations will be preserved. Continue?')) {
      return;
    }

    try {
      toast.info('Updating vaccination schedule...');
      const response = await axios.post(`${API}/vaccinations/reinitialize/${baby.id}`);
      toast.success(response.data.message);
      // Refresh vaccinations list
      await fetchVaccinations();
    } catch (error) {
      console.error('Error updating vaccination schedule:', error);
      toast.error('Failed to update vaccination schedule');
    }
  };

  const getVaccinationsByAgeGroup = () => {
    const grouped = {};
    
    // Only group vaccinations that have age_group (scheduled vaccinations)
    vaccinations.forEach(vaccination => {
      if (vaccination.age_group) {
        const ageGroup = vaccination.age_group;
        if (!grouped[ageGroup]) {
          grouped[ageGroup] = [];
        }
        grouped[ageGroup].push(vaccination);
      }
    });
    
    // Sort age groups in chronological order
    const ageGroupOrder = [
      'At Birth',
      '6 Weeks', 
      '10 Weeks',
      '14 Weeks',
      '6 Months',
      '7 Months', 
      '9 Months',
      '12 Months',
      '15 Months',
      '18 Months',
      '3 Years',
      '4-6 Years',
      'Yearly till 5 Years',
      '9-12 Years',
      'Other Vaccines'
    ];
    
    const sortedGroups = {};
    ageGroupOrder.forEach(ageGroup => {
      if (grouped[ageGroup]) {
        sortedGroups[ageGroup] = grouped[ageGroup];
      }
    });
    
    return sortedGroups;
  };

  // Get manually added vaccinations (those without age_group)
  const getManuallyAddedVaccinations = () => {
    return vaccinations.filter(v => !v.age_group || v.age_group === null);
  };

  // Toggle collapse state for a group
  const toggleGroupCollapse = (ageGroup) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [ageGroup]: !prev[ageGroup]
    }));
  };

  // Toggle all groups at once
  const toggleAllGroups = () => {
    const allGroups = Object.keys(getVaccinationsByAgeGroup());
    const allCollapsed = allGroups.every(group => collapsedGroups[group]);
    
    const newState = {};
    allGroups.forEach(group => {
      newState[group] = !allCollapsed;
    });
    setCollapsedGroups(newState);
  };

  // Check if all groups are collapsed
  const areAllGroupsCollapsed = () => {
    const allGroups = Object.keys(getVaccinationsByAgeGroup());
    return allGroups.length > 0 && allGroups.every(group => collapsedGroups[group]);
  };

  const handleMarkAsGiven = async () => {
    if (!editingVaccination) return;

    if (!formData.given_date) {
      toast.error('Please enter the vaccination date');
      return;
    }

    try {
      await axios.put(`${API}/vaccinations/${editingVaccination.id}`, {
        ...formData,
        status: 'given'
      });
      
      toast.success('Vaccination marked as given!');
      setEditingVaccination(null);
      setFormData({ given_date: '', batch_number: '', expiry_date: '', trade_name: '' });
      fetchVaccinations();
    } catch (error) {
      console.error('Error updating vaccination:', error);
      toast.error('Failed to update vaccination record');
    }
  };

  const handleAddVaccination = async (e) => {
    e.preventDefault();
    
    // Validate date format
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!dateRegex.test(addFormData.given_date)) {
      toast.error('Date given must be in DD/MM/YYYY format');
      return;
    }

    try {
      await axios.post(`${API}/vaccinations`, {
        baby_id: baby.id,
        vaccine_name: addFormData.vaccine_name,
        trade_name: addFormData.trade_name || undefined,
        given_date: addFormData.given_date,
        status: 'given'
      });
      
      toast.success('Vaccination added successfully!');
      setShowAddDialog(false);
      setAddFormData({ vaccine_name: '', trade_name: '', given_date: '' });
      fetchVaccinations();
    } catch (error) {
      console.error('Error adding vaccination:', error);
      toast.error('Failed to add vaccination');
    }
  };

  const getStatusBadge = (vaccination) => {
    const dueDate = new Date(vaccination.due_date);
    const today = new Date();
    
    if (vaccination.status === 'given') {
      return <Badge variant="outline" className="status-badge given">Given</Badge>;
    } else if (today > dueDate) {
      return <Badge variant="outline" className="status-badge overdue">Overdue</Badge>;
    } else {
      return <Badge variant="outline" className="status-badge due">Due</Badge>;
    }
  };

  const getStatusIcon = (vaccination) => {
    const dueDate = new Date(vaccination.due_date);
    const today = new Date();
    
    if (vaccination.status === 'given') {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    } else if (today > dueDate) {
      return <AlertCircle className="w-5 h-5 text-red-600" />;
    } else {
      return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const formatDate = (dateString) => {
    // If already in DD/MM/YYYY format, return as is
    if (dateString && dateString.includes('/')) {
      return dateString;
    }
    // Otherwise convert from ISO format
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getVaccinationProgress = () => {
    // Only count scheduled vaccinations (those with age_group) for progress
    const scheduledVaccinations = vaccinations.filter(v => v.age_group);
    const total = scheduledVaccinations.length;
    const given = scheduledVaccinations.filter(v => v.status === 'given').length;
    const percentage = total > 0 ? Math.round((given / total) * 100) : 0;
    return { total, given, percentage };
  };

  const progress = getVaccinationProgress();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vaccination records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white hover:bg-opacity-20"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Syringe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Vaccinations</h1>
                <p className="text-blue-200 text-sm">{baby.name}</p>
              </div>
            </div>
          </div>
          {!viewOnlyMode && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowAddDialog(true)}
                className="bg-white bg-opacity-20 text-white border-white border-opacity-30 hover:bg-white hover:bg-opacity-30"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Vaccination
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleUpdateSchedule}
                className="bg-white bg-opacity-20 text-white border-white border-opacity-30 hover:bg-white hover:bg-opacity-30"
              >
                Update Schedule
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Progress Overview */}
      <div className="px-6 pt-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-3" data-testid="vaccination-progress">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Vaccination Progress</h3>
              <p className="text-gray-600">{progress.given} of {progress.total} vaccinations completed</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{progress.percentage}%</div>
              <div className="text-sm text-gray-500">Complete</div>
            </div>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress.percentage}%` }}
            ></div>
          </div>
        </div>

        {/* Control Buttons - Below card, right aligned */}
        <div className="flex justify-end gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSchedule(!showSchedule)}
            className="text-sm"
          >
            {showSchedule ? (
              <>
                <EyeOff className="w-4 h-4 mr-1" />
                Hide Schedule
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-1" />
                Show Schedule
              </>
            )}
          </Button>
          
          {showSchedule && (
            <Button
              variant="outline"
              size="sm"
              onClick={toggleAllGroups}
              className="text-sm"
            >
              {areAllGroupsCollapsed() ? (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Expand All
                </>
              ) : (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Collapse All
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Vaccinations List */}
      <div className="px-6 pb-6">
        {showSchedule && vaccinations.length === 0 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center" data-testid="no-vaccinations">
            <Syringe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Vaccination Schedule</h3>
            <p className="text-gray-600">No vaccination records found for {baby.name}.</p>
          </div>
        )}
        
        {showSchedule && vaccinations.length > 0 && (
          <div className="space-y-6">
            {Object.entries(getVaccinationsByAgeGroup()).map(([ageGroup, groupVaccinations]) => (
              <div key={ageGroup} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-100 cursor-pointer hover:from-blue-100 hover:to-purple-100 transition-colors"
                  onClick={() => toggleGroupCollapse(ageGroup)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                        {ageGroup}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {groupVaccinations.filter(v => v.status === 'given').length} of {groupVaccinations.length} vaccines completed
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-4"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroupCollapse(ageGroup);
                      }}
                    >
                      {collapsedGroups[ageGroup] ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronUp className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </div>
                
                {!collapsedGroups[ageGroup] && (
                  <div className="divide-y divide-gray-100">
                    {groupVaccinations.map((vaccination) => (
                    <div key={vaccination.id} className="p-4" data-testid={`vaccination-${vaccination.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="flex-shrink-0 mt-1">
                            {getStatusIcon(vaccination)}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                                {vaccination.vaccine_name}
                              </h4>
                              {getStatusBadge(vaccination)}
                            </div>
                            
                            <div className="space-y-1 text-sm text-gray-600">
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4" />
                                <span>Due: {formatDate(vaccination.due_date)}</span>
                              </div>
                              {vaccination.given_date && (
                                <div className="text-green-600">Given: {formatDate(vaccination.given_date)}</div>
                              )}
                              {vaccination.trade_name && (
                                <div>Trade Name: {vaccination.trade_name}</div>
                              )}
                              {vaccination.batch_number && (
                                <div>Batch: {vaccination.batch_number}</div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {vaccination.status !== 'given' && !viewOnlyMode && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setEditingVaccination(vaccination)}
                                data-testid={`mark-given-${vaccination.id}`}
                              >
                                Mark as Given
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Mark Vaccination as Given</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <div className="text-center">
                                    <h3 className="font-semibold text-gray-900 mb-1">{vaccination.vaccine_name}</h3>
                                    <p className="text-sm text-gray-600 mb-2">Age Group: {vaccination.age_group}</p>
                                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                                      <Calendar className="w-4 h-4" />
                                      <span>Due Date: {formatDate(vaccination.due_date)}</span>
                                    </div>
                                    {vaccination.trade_name && (
                                      <p className="text-sm text-blue-600 mt-2">Trade Name: {vaccination.trade_name}</p>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="space-y-2">
                                  <Label htmlFor="given_date">Date Given *</Label>
                                  <Input
                                    id="given_date"
                                    type="text"
                                    placeholder="DD/MM/YYYY"
                                    value={formData.given_date}
                                    onChange={handleDateInput('given_date')}
                                    onKeyDown={handleDateKeyDown('given_date')}
                                    maxLength={10}
                                    data-testid="given-date-input"
                                  />
                                </div>
                                
                                <div className="space-y-2">
                                  <Label htmlFor="trade_name">Trade Name</Label>
                                  <Input
                                    id="trade_name"
                                    placeholder="Enter trade name (optional)"
                                    value={formData.trade_name || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, trade_name: e.target.value }))}
                                    data-testid="trade-name-input"
                                  />
                                </div>
                                
                                <div className="space-y-2">
                                  <Label htmlFor="batch_number">Batch Number</Label>
                                  <Input
                                    id="batch_number"
                                    placeholder="Enter batch number (optional)"
                                    value={formData.batch_number}
                                    onChange={(e) => setFormData(prev => ({ ...prev, batch_number: e.target.value }))}
                                    data-testid="batch-number-input"
                                  />
                                </div>
                                
                                <div className="space-y-2">
                                  <Label htmlFor="expiry_date">Expiry Date</Label>
                                  <Input
                                    id="expiry_date"
                                    type="text"
                                    placeholder="DD/MM/YYYY"
                                    value={formData.expiry_date}
                                    onChange={handleDateInput('expiry_date')}
                                    onKeyDown={handleDateKeyDown('expiry_date')}
                                    maxLength={10}
                                    data-testid="expiry-date-input"
                                  />
                                </div>
                                
                                <div className="flex space-x-2 pt-4">
                                  <Button 
                                    onClick={handleMarkAsGiven} 
                                    className="flex-1"
                                    data-testid="confirm-given-button"
                                  >
                                    Confirm
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    onClick={() => {
                                      setEditingVaccination(null);
                                      setFormData({ given_date: '', batch_number: '', expiry_date: '', trade_name: '' });
                                    }}
                                    className="flex-1"
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                    </div>
                  ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Manually Added Vaccinations - Always show regardless of showSchedule */}
        {getManuallyAddedVaccinations().length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                Additional Vaccinations
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Manually added vaccinations ({getManuallyAddedVaccinations().length})
              </p>
            </div>
            
            <div className="divide-y divide-gray-100">
              {getManuallyAddedVaccinations().map((vaccination) => (
                <div key={vaccination.id} className="p-4" data-testid={`manual-vaccination-${vaccination.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                            {vaccination.vaccine_name}
                          </h4>
                          <Badge variant="outline" className="status-badge given">Given</Badge>
                        </div>
                        
                        <div className="space-y-1 text-sm text-gray-600">
                          {vaccination.given_date && (
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4" />
                              <span className="text-green-600">Given: {formatDate(vaccination.given_date)}</span>
                            </div>
                          )}
                          {vaccination.trade_name && (
                            <div>Trade Name: {vaccination.trade_name}</div>
                          )}
                          {vaccination.batch_number && (
                            <div>Batch: {vaccination.batch_number}</div>
                          )}
                          {vaccination.notes && (
                            <div className="text-gray-500 italic">Notes: {vaccination.notes}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Vaccination Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Vaccination</DialogTitle>
            <DialogDescription>
              Add a new vaccination to {baby.name}'s schedule
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddVaccination} className="space-y-4">
            <div>
              <Label htmlFor="vaccine_name">Vaccine Name *</Label>
              <Input
                id="vaccine_name"
                value={addFormData.vaccine_name}
                onChange={(e) => setAddFormData({...addFormData, vaccine_name: e.target.value})}
                placeholder="e.g., BCG, Hepatitis B"
                required
              />
            </div>

            <div>
              <Label htmlFor="trade_name">Trade Name (Optional)</Label>
              <Input
                id="trade_name"
                value={addFormData.trade_name}
                onChange={(e) => setAddFormData({...addFormData, trade_name: e.target.value})}
                placeholder="e.g., Brand name"
              />
            </div>

            <div>
              <Label htmlFor="given_date">Date Given *</Label>
              <div className="relative">
                <Input
                  id="given_date"
                  value={addFormData.given_date}
                  onChange={handleAddDateInput('given_date')}
                  placeholder="DD/MM/YYYY"
                  maxLength={10}
                  required
                />
                <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Add Vaccination</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vaccinations;